import { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { DndContext, DragOverlay, DragStartEvent, DragEndEvent, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { LocationModal } from '@/components/charging/LocationModal';
import { RealisatieTimeline } from '@/components/charging/RealisatieTimeline';
import { SortableLocationCard, LocationCard } from '@/components/charging/LocationCard';
import { DroppableColumn } from '@/components/charging/DroppableColumn';
import { BulkActionsToolbar } from '@/components/charging/BulkActionsToolbar';
import { QuickTagPopover } from '@/components/charging/QuickTagPopover';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { KavelFaseStrip } from '@/components/charging/KavelFaseStrip';
import { SlapendSchakelaar } from '@/components/charging/SlapendSchakelaar';
import { useKavelSlaap } from '@/hooks/useKavelSlaap';
import { filterKavels, stadiaVanFase, stadiumLabel, telKavels } from '@/lib/chargingStages';

// De vier stadia van de fase Realisatie, in trajectvolgorde, uit de enige
// lijst die ze kent (T2.5). Alleen de kleur hoort bij dit scherm.
const REALISATIE_KLEUREN: Record<string, string> = {
  voorlopig_ontwerp: 'bg-blue-400',
  definitief_ontwerp: 'bg-blue-600',
  bouwvoorbereiding: 'bg-yellow-500',
  bouwuitvoering: 'bg-orange-500',
};

const REALISATIE_STAGES = stadiaVanFase('realisatie').map((id) => ({
  id,
  label: stadiumLabel(id),
  color: REALISATIE_KLEUREN[id] ?? 'bg-gray-400',
}));

export default function ChargingRealisatie() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  // Determine portal context
  const basePath = location.pathname.startsWith('/crm') ? '/crm/charging' : '/charging';
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState<string>('all');
  const [organizationFilter, setOrganizationFilter] = useState<string>('all');
  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<string>>(new Set());
  const [toonSlapend, setToonSlapend] = useState(false);
  const [tagPopup, setTagPopup] = useState<{ locationId: string; position: { x: number; y: number } } | null>(null);
  const { slaapIn, maakWakker, archiveerVeel, wijzig, wijzigVeel } = useKavelSlaap();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const { data: locations = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['charging-realisatie-locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('charging_locations')
        .select(`
          *,
          organizations(id, name)
        `)
        .in('stage', ['voorlopig_ontwerp', 'definitief_ontwerp', 'bouwvoorbereiding', 'bouwuitvoering'])
        .is('deleted_at', null)
        .order('order', { ascending: true });

      if (error) throw error;
      
      return (data || []).map(loc => ({
        ...loc,
        organization: loc.organizations
      }));
    },
  });

  // Zoeken/stad/organisatie eerst, slapend daarna — dan telt de schakelaar
  // over dezelfde set die op het scherm staat. Zie ChargingAcquisitie.
  const gezochteLocations = useMemo(() => {
    return locations.filter((location: any) => {
      const matchesSearch = !searchQuery ||
        location.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.organization?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.city?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCity = cityFilter === 'all' || location.city === cityFilter;
      const matchesOrg = organizationFilter === 'all' || location.org_id === organizationFilter;

      return matchesSearch && matchesCity && matchesOrg;
    });
  }, [locations, searchQuery, cityFilter, organizationFilter]);

  const telling = useMemo(
    () => telKavels(gezochteLocations, { toonSlapend }),
    [gezochteLocations, toonSlapend],
  );

  const filteredLocations = useMemo(
    () => filterKavels(gezochteLocations, { toonSlapend }),
    [gezochteLocations, toonSlapend],
  );

  const uniqueCities = useMemo(() => {
    const cities = locations.map((l: any) => l.city).filter(Boolean);
    return Array.from(new Set(cities));
  }, [locations]);

  const uniqueOrganizations = useMemo(() => {
    return Array.from(
      new Map(
        locations
          .filter((l: any) => l.organization)
          .map((l: any) => [l.organization.id, l.organization])
      ).values()
    );
  }, [locations]);

  const availableTags = useMemo(() => {
    return Array.from(
      new Set(locations.flatMap((l: any) => l.tags || []))
    ).sort();
  }, [locations]);

  const updateStageMutation = useMutation({
    mutationFn: async ({ locationId, newStage, insertBeforeId }: { locationId: string; newStage: string; insertBeforeId?: string }) => {
      let newOrder: number;

      if (insertBeforeId) {
        // Insert before specific location
        const { data: targetLocation } = await supabase
          .from('charging_locations')
          .select('order')
          .eq('id', insertBeforeId)
          .single();
        
        newOrder = targetLocation?.order || 0;
        
        // Shift other items down
        await supabase.rpc('shift_orders_down', {
          stage_name: newStage,
          from_order: newOrder
        });
      } else {
        // Append to end of stage
        const { data: maxOrderLocation } = await supabase
          .from('charging_locations')
          .select('order')
          .eq('stage', newStage)
          .is('deleted_at', null)
          .order('order', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        newOrder = (maxOrderLocation?.order || -1) + 1;
      }

      const { error } = await supabase
        .from('charging_locations')
        .update({ 
          stage: newStage, 
          order: newOrder,
          updated_at: new Date().toISOString() 
        })
        .eq('id', locationId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['charging-realisatie-locations'] });
      queryClient.invalidateQueries({ queryKey: ['charging-acquisitie-locations'] });
      toast({ title: 'Fase bijgewerkt' });
    },
    onError: (error) => {
      console.error('Update error:', error);
      toast({ title: 'Fout bij updaten', variant: 'destructive' });
    },
  });

  const reorderWithinColumnMutation = useMutation({
    mutationFn: async ({ locationId, targetLocationId, stage }: { locationId: string; targetLocationId: string; stage: string }) => {
      // Get all locations in this stage
      const { data: stageLocations } = await supabase
        .from('charging_locations')
        .select('id, order')
        .eq('stage', stage)
        .is('deleted_at', null)
        .order('order', { ascending: true });

      if (!stageLocations) return;

      const activeIndex = stageLocations.findIndex(l => l.id === locationId);
      const targetIndex = stageLocations.findIndex(l => l.id === targetLocationId);

      if (activeIndex === -1 || targetIndex === -1) return;

      // Reorder array
      const reordered = [...stageLocations];
      const [removed] = reordered.splice(activeIndex, 1);
      reordered.splice(targetIndex, 0, removed);

      // Batch update orders
      await Promise.all(
        reordered.map((loc, index) =>
          supabase
            .from('charging_locations')
            .update({ order: index, updated_at: new Date().toISOString() })
            .eq('id', loc.id)
        )
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['charging-realisatie-locations'] });
      queryClient.invalidateQueries({ queryKey: ['charging-acquisitie-locations'] });
    },
    onError: (error) => {
      console.error('Reorder error:', error);
      toast({ title: 'Fout bij herordenen', variant: 'destructive' });
    },
  });

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over) {
      setActiveId(null);
      return;
    }

    const activeId = active.id as string;
    const overId = over.id as string;
    
    const activeLocation = locations.find((l: any) => l.id === activeId);
    if (!activeLocation) {
      setActiveId(null);
      return;
    }

    const activeStage = activeLocation.stage;
    
    // Check if dropped on a stage column or another location
    const isStageColumn = REALISATIE_STAGES.some(s => s.id === overId);
    
    if (isStageColumn && overId !== activeStage) {
      // Moving to different stage (horizontal move)
      updateStageMutation.mutate({ 
        locationId: activeId, 
        newStage: overId
      });
    } else if (!isStageColumn) {
      // Dropped on another location (vertical reorder)
      const overLocation = locations.find((l: any) => l.id === overId);
      if (!overLocation) {
        setActiveId(null);
        return;
      }
      
      const overStage = overLocation.stage;
      const isSameColumn = activeStage === overStage;
      
      if (isSameColumn) {
        // Reordering within same column
        reorderWithinColumnMutation.mutate({
          locationId: activeId,
          targetLocationId: overId,
          stage: activeStage
        });
      } else {
        // Moving to different column + specific position
        updateStageMutation.mutate({
          locationId: activeId,
          newStage: overStage,
          insertBeforeId: overId
        });
      }
    }

    setActiveId(null);
  };

  // Scepticus-ronde 2: ook deze knop nam `if (!error)` als bewijs. Nul rijen
  // zonder error is een geblokkeerde schrijfactie, en die meldde "Opgeleverd!".
  const handleMoveToOperation = async (locationId: string) => {
    try {
      await wijzig(locationId, { stage: 'operatie' }, 'Naar Operatie verplaatsen');
      toast({ title: '✅ Opgeleverd! Verplaatst naar Operatie' });
    } catch {
      // useKavelSlaap toont de foutmelding al.
    }
  };

  // T2.5 / scepticus-ronde — telde niets en meldde altijd het volle aantal.
  // Zie dezelfde reparatie in ChargingAcquisitie.
  const handleBulkStageUpdate = async (newStage: string) => {
    await wijzigVeel(
      Array.from(selectedLocationIds),
      { stage: newStage },
      `verplaatst naar ${stadiumLabel(newStage)}`,
    );
    setSelectedLocationIds(new Set());
  };

  const handleBulkOwnerUpdate = async (ownerId: string) => {
    await wijzigVeel(Array.from(selectedLocationIds), { created_by: ownerId }, 'toegewezen');
    setSelectedLocationIds(new Set());
  };

  // Archiveren loopt via `useKavelSlaap.archiveerVeel`: rijen tellen, één
  // melding met de uitkomst, en `dormant_at` alleen meewissen als de kolom
  // bestaat (zodat het ook zonder migratie 084 werkt).
  const handleBulkDelete = async () => {
    await archiveerVeel(Array.from(selectedLocationIds));
    setSelectedLocationIds(new Set());
  };

  const activeLocation = activeId ? locations.find(l => l.id === activeId) : null;

  return (
    <div className="container mx-auto p-4 sm:p-6 w-full max-w-full">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Realisatie</h1>
            <p className="text-muted-foreground">Van ontwerp tot oplevering</p>
          </div>
          <Button onClick={() => setIsLocationModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nieuwe Locatie
          </Button>
        </div>

        {/* De vier fasen als hoofdindeling (T2.5). */}
        <KavelFaseStrip actief="realisatie" basePath={basePath} />

        <SlapendSchakelaar
          telling={telling}
          toonSlapend={toonSlapend}
          onToonSlapend={setToonSlapend}
        />

        {/* Tabs */}
        <Tabs defaultValue="kanban" className="space-y-6">
          <TabsList>
            <TabsTrigger value="kanban">Kanban</TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
          </TabsList>

          {/* Filters */}
          <Card className="p-4">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Zoek locaties..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={cityFilter} onValueChange={setCityFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Alle steden" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle steden</SelectItem>
                  {uniqueCities.map((city: string) => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={organizationFilter} onValueChange={setOrganizationFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Alle organisaties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle organisaties</SelectItem>
                  {uniqueOrganizations.map((org: any) => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </Card>

          <TabsContent value="kanban" className="mt-6">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {REALISATIE_STAGES.map((stage) => {
                  const stageLocations = filteredLocations.filter((l: any) => l.stage === stage.id);

                  return (
                    <DroppableColumn key={stage.id} stage={stage}>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${stage.color}`} />
                          <h3 className="font-semibold">{stage.label}</h3>
                        </div>
                        <Badge variant="secondary">{stageLocations.length}</Badge>
                      </div>

                      <SortableContext
                        id={stage.id}
                        items={stageLocations.map((l: any) => l.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-3 min-h-[200px]">
                          {stageLocations.map((location: any) => (
                            <SortableLocationCard
                              key={location.id}
                              location={location}
                              onClick={() => navigate(`${basePath}/locatie/${location.id}`)}
                              stage={stage.id}
                              onMoveToOperation={handleMoveToOperation}
                              onSlaapIn={slaapIn}
                              onMaakWakker={maakWakker}
                              isSelected={selectedLocationIds.has(location.id)}
                              onSelectionChange={(checked) => {
                                const newSet = new Set(selectedLocationIds);
                                if (checked) {
                                  newSet.add(location.id);
                                } else {
                                  newSet.delete(location.id);
                                }
                                setSelectedLocationIds(newSet);
                              }}
                              onLongPress={(id, pos) => setTagPopup({ locationId: id, position: pos })}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DroppableColumn>
                  );
                })}
              </div>

              <DragOverlay>
                {activeLocation ? <LocationCard location={activeLocation} /> : null}
              </DragOverlay>
            </DndContext>
          </TabsContent>

          <TabsContent value="timeline" className="mt-6">
            <RealisatieTimeline projects={filteredLocations} />
          </TabsContent>
        </Tabs>

        {selectedLocationIds.size > 0 && (
          <BulkActionsToolbar
            selectedCount={selectedLocationIds.size}
            onClearSelection={() => setSelectedLocationIds(new Set())}
            onBulkStageUpdate={handleBulkStageUpdate}
            onBulkOwnerUpdate={handleBulkOwnerUpdate}
            onBulkDelete={handleBulkDelete}
            stages={REALISATIE_STAGES}
          />
        )}

        {isLoading && <div className="text-center py-8">Laden...</div>}
        {isError && <div className="text-center py-8 text-destructive">Fout bij laden</div>}
      </div>

      <LocationModal
        isOpen={isLocationModalOpen}
        onClose={() => {
          setIsLocationModalOpen(false);
          queryClient.invalidateQueries({ queryKey: ['charging-realisatie-locations'] });
        }}
        initialStage="voorlopig_ontwerp"
      />

      {/* Quick Tag Popover */}
      {tagPopup && (
        <QuickTagPopover
          open={!!tagPopup}
          onOpenChange={(open) => !open && setTagPopup(null)}
          locationId={tagPopup.locationId}
          locationName={locations.find((l: any) => l.id === tagPopup.locationId)?.name || ''}
          currentTags={locations.find((l: any) => l.id === tagPopup.locationId)?.tags || []}
          availableTags={availableTags}
          position={tagPopup.position}
          queryKey={['charging-realisatie-locations']}
        />
      )}
    </div>
  );
}
